// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import { sendAutocompleteEvent } from "@insite/client-framework/OdpEvents/AutocompleteEvent";
import {
    addSearchHistory,
    getSearchHistory,
    SearchHistoryItem,
} from "@insite/client-framework/Services/AutocompleteService";
import getAutocompleteModel from "@insite/client-framework/Store/CommonHandlers/GetAutocompleteModel";
import sendAutocompleteOdpEvent from "@insite/client-framework/Store/CommonHandlers/SendAutocompleteOdpEvent";
import sendSearchTracking from "@insite/client-framework/Store/CommonHandlers/SendSearchTracking";
import setNavDrawerIsOpen from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetNavDrawerIsOpen";
import {
    getCurrentUserIsGuest,
    getSession,
    getSettingsCollection,
} from "@insite/client-framework/Store/Context/ContextSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { AutocompleteItemModel, AutocompleteModel } from "@insite/client-framework/Types/ApiModels";
import AutocompleteBrands, {
    AutocompleteBrandsStyles,
} from "@insite/content-library/Widgets/Header/AutocompleteBrands";
import AutocompleteCategories, {
    AutocompleteCategoriesStyles,
} from "@insite/content-library/Widgets/Header/AutocompleteCategories";
import AutocompleteContent, {
    AutocompleteContentStyles,
} from "@insite/content-library/Widgets/Header/AutocompleteContent";
import AutocompleteProducts, {
    AutocompleteProductsStyles,
} from "@insite/content-library/Widgets/Header/AutocompleteProducts";
import AutocompleteSuggestions, {
    AutocompleteSuggestionsStyles,
} from "@insite/content-library/Widgets/Header/AutocompleteSuggestions";
import SearchHistory, { SearchHistoryStyles } from "@insite/content-library/Widgets/Header/SearchHistory";
import { StyledButton } from "@insite/mobius/Clickable";
import { FormFieldStyle } from "@insite/mobius/FormField";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { IconPresentationProps, IconWrapper } from "@insite/mobius/Icon";
import { Clickable, Hidden, Icon, LoadingSpinner } from "@insite/mobius/index";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Popover, {
    ContentBodyProps,
    OverflowWrapperProps,
    PopoverPresentationProps,
    PositionStyle,
} from "@insite/mobius/Popover";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import setShowDropShadow from "@rc/App/Handlers/XNG/showDropShadow";
import { layoutContainer } from "@rc/App/SharedStyles/sharedStyles";
import { CustomAppState, xHeaderActions } from "@rc/App/Store/Reducers";
import { screen } from "@rc/XDL/Helpers";
import SearchInactive from "@rc/XDL/Icons/SearchInactive";
import { color, xScreen } from "@rc/XDL/Utilities";
import debounce from "lodash/debounce";
import throttle from "lodash/throttle";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import { Dispatch } from "redux";
import { css, ThemeProps, withTheme } from "styled-components";

interface OwnProps {
    /** A unique identifier to assist with id-generation for `aria-controls`. */
    readonly id: string;
    inputRef?: React.Ref<HTMLInputElement>;
    autocompletePositionFunction?: (element: React.RefObject<HTMLUListElement>) => PositionStyle;
    onBeforeGoToUrl?: () => void;
    extendedStyles?: SearchInputStyles;
    isAnimated?: boolean;
}

const mapStateToProps = (state: CustomAppState) => {
    const {
        searchSettings: { autocompleteEnabled, searchHistoryEnabled, searchHistoryLimit, searchPath },
    } = getSettingsCollection(state);
    const currentUserIsGuest = getCurrentUserIsGuest(state);
    const userName = state.context.session.userName;
    const session = getSession(state);
    return {
        autocompleteEnabled,
        searchHistoryEnabled,
        searchHistoryLimit,
        location: getLocation(state),
        searchPath,
        searchInputFocus: state.xHeader.searchInputFocus,
        isAuthenticated: userName && !currentUserIsGuest,
        signInPageUrl: getPageLinkByPageType(state, "SignInPage")?.url,
    };
};

const mapDispatchToProps = {
    getAutocompleteModel,
    sendSearchTracking,
    sendAutocompleteOdpEvent,
    setSearchInputStatus: (focus: boolean) => (dispatch: Dispatch<xHeaderActions>) => {
        dispatch({ type: "xHeader/searchInputFocus", focus });
    },
    setNavDrawerIsOpen,
    setShowDropShadow,
};

type Props = OwnProps &
    ThemeProps<BaseTheme> &
    HasHistory &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps>;

interface State {
    query: string;
    focusableAutocompleteItems: AutocompleteItemModel[];
    focusedAutocompleteItem?: AutocompleteItemModel;
    focusedSearchHistoryQuery?: string;
    autoCompleteModel?: AutocompleteModel;
    isLoading: boolean;
    clickedProduct: boolean;
}

const DOWN_KEY = 40;
const UP_KEY = 38;
const ENTER_KEY = 13;

export interface SearchInputStyles {
    input?: TextFieldPresentationProps;
    popover?: PopoverPresentationProps;
    popoverWrapper?: OverflowWrapperProps;
    popoverContentBody?: ContentBodyProps;
    autocompleteWrapper?: InjectableCss;
    autocompleteColumnWrapper?: InjectableCss;
    autocompleteCommerceSearchWrapper?: InjectableCss;
    searchHistoryStyles?: SearchHistoryStyles;
    autocompleteSuggestionsStyles?: AutocompleteSuggestionsStyles;
    autocompleteCategoriesStyles?: AutocompleteCategoriesStyles;
    autocompleteBrandsStyles?: AutocompleteBrandsStyles;
    autocompleteContentStyles?: AutocompleteContentStyles;
    autocompleteProductsStyles?: AutocompleteProductsStyles;
    searchInputWrapper?: InjectableCss;
    autocompleteLeftColumnWrapper?: InjectableCss;
    autocompleteRightColumnWrapper?: InjectableCss;
    viewAll?: LinkPresentationProps;
    iconStyle?: IconPresentationProps;
}

export const searchInputStyles: SearchInputStyles = {
    input: {
        sizeVariant: "default",
        iconProps: { src: SearchInactive },
        border: "underline",
        cssOverrides: {
            formField: css`
                width: 310px;
                ${xScreen("lte", "md")} {
                    display: none;
                }

                &&& {
                    input {
                        min-height: auto;
                        height: 41px;
                        border: 1px solid ${color("common.ui5")};
                        border-radius: 20px;
                        background-color: ${color("common.background")};
                        color: ${color("common.ui5")};
                        font-size: 16px;
                        letter-spacing: 0;
                        line-height: 17px;
                        font-weight: 500;
                        &::placeholder {
                            color: ${color("secondary")};
                            font-weight: 300;
                        }
                    }
                    ${IconWrapper} {
                        top: 3px;
                    }
                }
            `,
        },
    },
    popover: {
        transitionDuration: "short",
        toggle: false,
        css: css`
            background-color: ${color("common.ui1")};
            box-shadow: none;
            ${xScreen("lte", "sm")} {
                max-height: 85%;
            }
        `,
    } as any,
    popoverWrapper: {
        as: "div",
        _height: "auto",
        css: css`
            display: inline-flex;
            justify-content: flex-end;
        `,
    },
    popoverContentBody: {
        as: "div",
        _height: "100%",
    },
    autocompleteWrapper: {
        css: css`
            display: flex;
            overflow-x: hidden;
            overflow-y: auto;
            max-height: calc(100vh - 50px);
            padding: 15px 0 20px 0;
            ${layoutContainer}
            &::before {
                content: "";
                display: block;
                height: 1px;
                width: 100%;
                position: absolute;
                top: 0;
                left: 0;
                background: ${color("common.border")};
                ${screen("lte", "md")`
                    height: 0;
                `}
            }
            ${screen("lte", "xs")`
                flex-direction: column;
                max-height: 100vh;
                height: auto;
            `};

            &:empty {
                display: none;
            }
        `,
    },
    autocompleteColumnWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 375px;
            padding: 0 20px 15px 20px;
        `,
    },
    autocompleteCommerceSearchWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            padding: 0 20px 15px 20px;
        `,
    },
    searchInputWrapper: {
        css: css`
            width: 100%;

            .close-search-btn {
                &:hover svg {
                    color: ${color("primary")};
                }
            }

            ${({ searchInputFocus }: any) =>
                searchInputFocus &&
                `
                &&& {
                    ${StyledButton}, .loader {
                        position: absolute;
                        right: -5px;
                        top: 33px;
                        padding: 5px;
                        cursor: pointer;
                        z-index: 9;
                    }
                    ${FormFieldStyle} {
                        width: 100%;
                        max-width: 100%;
                        display: block;
                        top: 6px;
                        z-index: 9;

                        input {
                            border: 0;
                            background: transparent;
                            padding: 0 50px;
                            min-height: 80px;
                        }
                        .clickable-icon {
                            right: auto;
                            left: 0;
                            top: 20px;
                            padding: 0;
                            ${IconWrapper} {
                                right: 10px;
                            }
                        }
                    }
                }
            `}

            ${xScreen("lte", "md")} {
                ${({ searchInputFocus }: any) =>
                searchInputFocus &&
                `
                    &&& {
                        ${StyledButton}, .loader{
                            top: 15px;
                        }

                        ${FormFieldStyle} {
                            top: -1px;

                            input {
                                padding: 0 40px;
                                min-height: 60px;
                            }

                            .clickable-icon {
                                top: 10px;
                            }
                        }
                    }
                `}
            }
        `,
    },
    autocompleteLeftColumnWrapper: {
        css: css`
            width: 25%;
            padding: 0 35px 0 0;
            text-align: left;
            position: relative;
            &:after {
                content: " ";
                position: absolute;
                height: 100%;
                width: 1px;
                background-color: ${color("common.border")};
                right: 0;
                top: 0;
            }
            &:not(.split-two-views) {
                width: 100%;
                &:after {
                    background: transparent;
                }
            }
            ${screen("lte", "sm")`
                width: 50%;
            `}
            ${screen("lte", "xs")`
                width: 100%;
                padding: 0;
                &.split-two-views {
                    padding-bottom: 35px;
                }
                &:after {
                    width: 100%;
                    height: 1px;
                    bottom: 0;
                    top: auto;
                }
            `}
        `,
    },
    autocompleteRightColumnWrapper: {
        css: css`
            width: 75%;
            padding: 0 0 0 15px;
            text-align: left;
            &:not(.split-two-views) {
                width: 100%;
            }
            ${screen("lte", "sm")`
                width: 50%;
            `}
            ${screen("lte", "xs")`
                width: 100%;
                padding: 0;
                &.split-two-views {
                    padding-top: 35px;
                    padding-bottom: 13px;
                }
            `}
        `,
    },
    viewAll: {
        typographyProps: {
            weight: 500,
            size: 16,
            lineHeight: "17px",
            ellipsis: true,
        },
    },
    iconStyle: {
        size: 20,
        src: "SearchInactive",
        color: "secondary",
        css: css`
            margin-top: 4px;
        `,
    },
};

const styles = searchInputStyles;

class SearchInput extends React.Component<Props, State> {
    private readonly styles: SearchInputStyles;

    constructor(props: Props) {
        super(props);

        this.styles = mergeToNew(styles, props.extendedStyles);
        const parsedQuery = parseQueryString<{ query?: string }>(this.props.location.search);

        this.state = {
            query: parsedQuery.query || "",
            focusableAutocompleteItems: [],
            isLoading: false,
            clickedProduct: true,
        };
    }

    list = React.createRef<HTMLUListElement>();
    popover = React.createRef<any>();
    showAutocomplete = true;
    searchHistory = getSearchHistory();

    componentDidUpdate(prevProps: Props, prevState: State) {
        if (this.props.location.pathname !== prevProps.location.pathname) {
            // clear the search box on navigate away from search
            if (!this.props.location.pathname.toLowerCase().startsWith(`/${this.props.searchPath.toLowerCase()}`)) {
                // TODO ISC-12679 - Redesign so that this can be moved to a field of the class rather than state.
                // eslint-disable-next-line react/no-did-update-set-state
                this.setState({ query: "" });
            } else {
                this.closeAutocomplete();
            }
        }
        if (
            this.state.autoCompleteModel &&
            prevState.autoCompleteModel &&
            this.state.autoCompleteModel !== prevState.autoCompleteModel
        ) {
            // xng-change: we don't need to update the items because we always have products at the bottom
            // this.updateFocusableItems();
        }
    }

    // xng-change: we don't need to attach the resize event because we always have products at the bottom
    // componentDidMount() {
    //     window.addEventListener("resize", this.handleResize, true);
    // }

    // xng-changes: don't need this because we didn't attach the resize event
    // componentWillUnmount() {
    //     window.removeEventListener("resize", this.handleResize);
    // }

    handleResize = () => {
        this.updateFocusableItems();
    };

    updateFocusableItems = () => {
        if (this.state.isLoading || !this.state.autoCompleteModel) {
            return;
        }

        const { categories, brands, content, products } = this.state.autoCompleteModel;
        const isProductsOnTop = window.innerWidth < this.props.theme.breakpoints.values[3];
        const newFocusableOptions = isProductsOnTop
            ? (products || ([] as AutocompleteItemModel[]))
                .concat(categories || [])
                .concat(brands || [])
                .concat(content || [])
            : (categories || [])
                .concat(brands || [])
                .concat(content || [])
                .concat(products || []);
        this.setState({
            focusableAutocompleteItems: newFocusableOptions,
            focusedAutocompleteItem: undefined,
            focusedSearchHistoryQuery: undefined,
        });
    };

    doSearch = () => {
        if (this.state.query.trim() === "") {
            return;
        }

        if (!this.props.isAuthenticated) {
            this.props.history.push(this.props.signInPageUrl || "");
        }

        this.showAutocomplete = false;
        if (this.state.autoCompleteModel?.products?.length === 1) {
            this.goToUrl(this.state.autoCompleteModel?.products[0].url);
        } else {
            const encodedQuery = encodeURIComponent(this.state.query);
            this.goToUrl(`/${this.props.searchPath}?query=${encodedQuery}`, false);

            if (this.props.location.pathname.toLowerCase().startsWith(`/${this.props.searchPath.toLowerCase()}`)) {
                // if we already on the Search page then scroll to top
                window.scrollTo(0, 0);
            }
        }
        if (this.props.searchHistoryEnabled) {
            addSearchHistory(this.state.query, this.props.searchHistoryLimit);
        }

        this.inputFocusStoreReset();
    };

    loadAutocomplete = debounce((query: string) => {
        if (!this.props.isAuthenticated) {
            return;
        }
        if (query.length > 0) {
            this.setState({ isLoading: true });
        }
        this.props.getAutocompleteModel({
            query,
            signInPageUrl: this.props.signInPageUrl,
            onSuccess: (result: AutocompleteModel) => {
                if (this.showAutocomplete) {
                    this.setState({
                        autoCompleteModel: result,
                        isLoading: false,
                    });
                    this.openAutocomplete();
                }
            },
            onComplete(resultProps: any) {
                if (resultProps.apiResult) {
                    this.onSuccess?.(resultProps.apiResult);
                }
            },
        } as any);
    }, 200);

    handleInputChange = (event: React.ChangeEvent<HTMLInputElement>, toggleAutoComplete = true) => {
        this.showAutocomplete = toggleAutoComplete;
        const newQuery = event.target.value;
        this.setState({ query: newQuery });
        this.loadAutocomplete(newQuery);
    };

    openAutocomplete = () => {
        this.popover.current && (this.popover.current! as any).openPopover();
    };

    closeAutocomplete = () => {
        this.popover.current && (this.popover.current! as any).closePopover();
        this.inputFocusStoreReset();
    };

    getPopoverPosition = (element: React.RefObject<HTMLUListElement>) => {
        const headerElement = document.getElementsByTagName("header")[0].getBoundingClientRect();

        if (this.props.autocompletePositionFunction) {
            return this.props.autocompletePositionFunction(element);
        }

        const positionStyle: PositionStyle = {
            position: "fixed",
        };

        const windowSize = window.innerWidth;
        if (element.current) {
            const rect = element.current.getBoundingClientRect();
            positionStyle.right = "0";
            positionStyle.width = "100%";

            if (windowSize >= 1025) {
                positionStyle.top = `${headerElement.height - 30}px`;
            } else {
                positionStyle.top = headerElement.height;
            }
        }

        return positionStyle;
    };

    moveFocus = throttle((direction: "prev" | "next") => {
        if (this.state.query) {
            this.setState(({ focusedAutocompleteItem, focusableAutocompleteItems }) => {
                const focusableQueue =
                    direction === "prev" ? focusableAutocompleteItems.slice().reverse() : focusableAutocompleteItems;
                const focusedIndex = focusableQueue.indexOf(focusedAutocompleteItem as AutocompleteItemModel);
                const newFocusedOption = focusableQueue.find((_, index) => index > focusedIndex);
                return { focusedAutocompleteItem: newFocusedOption };
            });
        } else {
            this.setState(({ focusedSearchHistoryQuery }) => {
                const searchHistoryItems = getSearchHistory();
                const focusableQueue = direction === "prev" ? searchHistoryItems.slice().reverse() : searchHistoryItems;
                const focusedSearchHistoryItem = focusableQueue.find(o => o.query === focusedSearchHistoryQuery);
                const focusedIndex = focusableQueue.indexOf(focusedSearchHistoryItem as SearchHistoryItem);
                const newFocusedOption = focusableQueue.find((_, index) => index > focusedIndex);
                return { focusedSearchHistoryQuery: newFocusedOption?.query };
            });
        }
    }, 100);

    handlePopoverKeyDown = (event: KeyboardEvent) => {
        switch (event.keyCode) {
            case DOWN_KEY:
                this.moveFocus("next");
                break;
            case UP_KEY:
                this.moveFocus("prev");
                break;
            case ENTER_KEY:
                // eslint-disable-next-line no-case-declarations
                const searchId = `headerSearch-${this.props.id}`;
                document?.getElementById(searchId)?.blur();
                if (this.state.focusedAutocompleteItem) {
                    this.goToUrl(this.state.focusedAutocompleteItem.url);
                } else if (this.state.focusedSearchHistoryQuery) {
                    this.setState({ query: this.state.focusedSearchHistoryQuery }, () => {
                        this.doSearch();
                    });
                } else {
                    this.doSearch();
                }
                break;
            default:
                break;
        }
    };

    handlePopoverClose = () => {
        document.body.classList.remove("lock");

        if (!this.state.clickedProduct && (this.state.autoCompleteModel?.products?.length ?? 0) > 0) {
            this.props.sendAutocompleteOdpEvent({});
        }

        this.setState({
            clickedProduct: false,
        });

        this.setState({
            focusedAutocompleteItem: undefined,
            focusedSearchHistoryQuery: undefined,
            query: "",
        });
        this.inputFocusStoreReset();
    };

    trackAutocompleteSearchResults = () => {
        // Make sure only to trigger for autocomplete results
        if (!this.state.query || !this.props.autocompleteEnabled || !this.state.autoCompleteModel) {
            return;
        }

        const { categories, brands, content, products } = this.state.autoCompleteModel;
        const resultCount =
            (products?.length ?? 0) + (categories?.length || 0) + (content?.length || 0) + (brands?.length || 0);
        if (resultCount > 0) {
            // Track an autocomplete of 1+ items
            this.props.sendSearchTracking({
                searchEvent: "autocompleteSearchResults",
                searchTerm: this.state.query,
                resultCount,
                productCount: products?.length,
                categoryCount: categories?.length,
                contentCount: content?.length,
                brandCount: brands?.length,
            });
            return;
        }

        // Track zero resultCount search results and triggers from this.doSearch
        this.props.sendSearchTracking({
            searchEvent: "searchResults",
            searchTerm: this.state.query,
            resultCount,
        });
    };

    goToUrl = (url: string, track = true) => {
        const data = {
            searchQuery: this.state.query,
            searchPosition:
                this.state.autoCompleteModel?.suggestions?.findIndex(o => o.url === url) ??
                this.state.autoCompleteModel?.products?.findIndex(o => o.url === url) ??
                -1,
            attributionToken: this.state.autoCompleteModel?.attributionToken,
        };

        if (data.searchPosition >= 0) {
            this.props.sendAutocompleteOdpEvent(data);
            this.setState({ clickedProduct: true });
        }

        this.props.onBeforeGoToUrl && this.props.onBeforeGoToUrl();
        if (track) {
            this.trackAutocompleteSearchResults();
        }
        this.closeAutocomplete();
        this.setState({
            // don't use stale results on next search and go to wrong PDP
            autoCompleteModel: undefined,
        });
        this.props.history.push(url);
    };

    closeNavDrawer = () => {
        this.props.setNavDrawerIsOpen({ navDrawerIsOpen: false });
    };

    inputFocusSet = (event?: any) => {
        document.body.classList.add("lock");
        this.showAutocomplete = true;
        this.props.setShowDropShadow({ showDropShadow: true });
        this.closeNavDrawer();
        this.props.setSearchInputStatus(true);
    };

    inputFocusStoreReset = () => {
        setTimeout(() => {
            this.props.setSearchInputStatus(false);
            this.props.setShowDropShadow({ showDropShadow: false });
        }, 100);
    };

    closeAutoCompleteAndPopover = () => {
        this.handlePopoverClose();
        this.closeAutocomplete();
        this.inputFocusStoreReset();
        this.showAutocomplete = false;
    };

    executeCancelSearch = (event: any) => {
        event.preventDefault();
        event.stopPropagation();
        this.setState({ query: "" });
        this.inputFocusStoreReset();
        this.handlePopoverClose();
        this.closeAutocomplete();
    };

    renderViewAll = () => {
        return (
            <Link onClick={this.doSearch} {...styles.viewAll}>
                {translate("View All")}
            </Link>
        );
    };

    renderAutocomplete = () => {
        if (!this.props.autocompleteEnabled) {
            return null;
        }

        if (!this.state.autoCompleteModel) {
            return null;
        }

        const {
            categories,
            brands,
            content,
            products,
            suggestions,
            popularBrands,
            popularCategories,
            isRetailSearchCompletionResults,
        } = this.state.autoCompleteModel;

        const resultCount =
            (products?.length ?? 0) + (categories?.length || 0) + (content?.length || 0) + (brands?.length || 0);

        if (!resultCount) {
            return null;
        }

        return (
            <>
                {isRetailSearchCompletionResults &&
                    ((suggestions && suggestions.length > 0) ||
                        (categories && categories.length > 0) ||
                        (popularCategories && popularCategories.length > 0) ||
                        (brands && brands.length > 0) ||
                        (popularBrands && popularBrands.length > 0)) && (
                        <StyledWrapper
                            {...this.styles.autocompleteCommerceSearchWrapper}
                            className="autocomplete-commerce-search-wrapper"
                        >
                            {suggestions && suggestions.length > 0 && (
                                <AutocompleteSuggestions
                                    suggestions={suggestions}
                                    focusedItem={this.state.focusedAutocompleteItem}
                                    goToUrl={this.goToUrl}
                                    extendedStyles={this.styles.autocompleteSuggestionsStyles}
                                />
                            )}
                            {((categories && categories.length > 0) ||
                                (popularCategories && popularCategories.length > 0)) && (
                                    <AutocompleteCategories
                                        categories={categories}
                                        popularCategories={popularCategories}
                                        focusedItem={this.state.focusedAutocompleteItem}
                                        goToUrl={this.goToUrl}
                                        extendedStyles={this.styles.autocompleteCategoriesStyles}
                                        isRetailSearchCompletionResults={isRetailSearchCompletionResults}
                                    />
                                )}
                            {((brands && brands.length > 0) || (popularBrands && popularBrands.length > 0)) && (
                                <AutocompleteBrands
                                    brands={brands}
                                    popularBrands={popularBrands}
                                    focusedItem={this.state.focusedAutocompleteItem}
                                    goToUrl={this.goToUrl}
                                    extendedStyles={this.styles.autocompleteBrandsStyles}
                                    isRetailSearchCompletionResults={isRetailSearchCompletionResults}
                                />
                            )}
                        </StyledWrapper>
                    )}
                {!isRetailSearchCompletionResults && (
                    <StyledWrapper {...this.styles.autocompleteWrapper} className="autocomplete-wrapper">
                        {((categories && categories.length > 0) ||
                            (brands && brands.length > 0) ||
                            (content && content.length > 0)) && (
                                <StyledWrapper
                                    {...this.styles.autocompleteLeftColumnWrapper}
                                    className={`autocomplete-column-wrapper ${products && products.length > 0 ? "split-two-views" : ""
                                        }`}
                                >
                                    <AutocompleteCategories
                                        categories={categories}
                                        focusedItem={this.state.focusedAutocompleteItem}
                                        goToUrl={this.goToUrl}
                                        extendedStyles={this.styles.autocompleteCategoriesStyles}
                                    />
                                    <AutocompleteBrands
                                        brands={brands}
                                        focusedItem={this.state.focusedAutocompleteItem}
                                        goToUrl={this.goToUrl}
                                        extendedStyles={this.styles.autocompleteBrandsStyles}
                                    />
                                    <AutocompleteContent
                                        content={content}
                                        focusedItem={this.state.focusedAutocompleteItem}
                                        goToUrl={this.goToUrl}
                                        extendedStyles={this.styles.autocompleteContentStyles}
                                    />
                                    {this.renderViewAll()}
                                </StyledWrapper>
                            )}
                        {products && products.length > 0 && (
                            <StyledWrapper
                                {...this.styles.autocompleteRightColumnWrapper}
                                className={`autocomplete-column-wrapper ${(categories && categories.length > 0) ||
                                    (brands && brands.length > 0) ||
                                    (content && content.length > 0)
                                    ? "split-two-views"
                                    : ""
                                    }`}
                            >
                                <AutocompleteProducts
                                    products={products}
                                    focusedItem={this.state.focusedAutocompleteItem}
                                    goToUrl={this.goToUrl}
                                    extendedStyles={this.styles.autocompleteProductsStyles}
                                />
                                {this.renderViewAll()}
                            </StyledWrapper>
                        )}
                    </StyledWrapper>
                )}
            </>
        );
    };

    render() {
        const styles = this.styles;
        const searchId = `headerSearch-${this.props.id}`;
        const searchInputFocus = this.props.searchInputFocus;

        const searchInput = (
            <StyledWrapper {...({ searchInputFocus } as any)} {...styles.searchInputWrapper}>
                <TextField
                    {...styles.input}
                    ref={this.props.inputRef}
                    iconClickableProps={{
                        onClick: () => {
                            this.inputFocusSet();
                            this.doSearch();
                        },
                        className: "clickable-icon",
                        "aria-roledescription": `Submit Search`,
                        id: `headerSearchButton`,
                    }}
                    value={this.state.query}
                    onChange={this.handleInputChange}
                    onFocus={this.inputFocusSet}
                    placeholder={translate("Search for Product or Article")}
                    autoComplete="off"
                    clickableText={translate("submit search")}
                    id={searchId}
                    data-test-selector="headerSearchInputTextField"
                    className="search-input"
                    aria-multiline="false"
                    aria-required="false"
                    {...({ searchInputFocus } as any)}
                    maxLength={100}
                />
                {searchInputFocus && (
                    <>
                        {this.state.isLoading ? (
                            <StyledWrapper
                                css={css`
                                    height: 17px;
                                    display: flex;
                                    align-items: center;

                                    svg {
                                        width: 22px;
                                        height: 22px;
                                    }
                                `}
                                className="loader"
                            >
                                <LoadingSpinner />
                            </StyledWrapper>
                        ) : (
                            <Clickable className="close-search-btn" onClick={this.executeCancelSearch}>
                                <Icon size={15} src="MenuCloseInactive" color="common.ui5" key="close-search" />
                                <VisuallyHidden>{translate("close-search")}</VisuallyHidden>
                            </Clickable>
                        )}
                    </>
                )}
                {!searchInputFocus && (
                    <Hidden
                        above="md"
                        css={css`
                            margin-left: 20px;
                        `}
                    >
                        <Clickable
                            onClick={() => {
                                this.inputFocusSet();
                                this.doSearch();
                            }}
                        >
                            <Icon {...styles.iconStyle} key="search" />
                            <VisuallyHidden>{translate("search")}</VisuallyHidden>
                        </Clickable>
                    </Hidden>
                )}
            </StyledWrapper>
        );

        return (
            <>
                <VisuallyHidden as="label" htmlFor={searchId} id={`${searchId}-label`}>
                    {translate("Site Search")}
                </VisuallyHidden>
                <Popover
                    {...styles.popover}
                    controlsId={`${searchId}-popover`}
                    ref={this.popover}
                    insideRefs={[this.list]}
                    popoverTrigger={searchInput}
                    positionFunction={this.getPopoverPosition}
                    wrapperProps={styles.popoverWrapper}
                    contentBodyProps={
                        {
                            ref: this.list,
                            ...styles.popoverContentBody,
                        } as any
                    }
                    handleKeyDown={this.handlePopoverKeyDown}
                    onClose={this.handlePopoverClose}
                    className="popover"
                >
                    {!this.state.query && this.showAutocomplete && this.searchHistory?.length > 0 && (
                        <StyledWrapper {...this.styles.autocompleteWrapper}>
                            <SearchHistory
                                focusedQuery={this.state.focusedSearchHistoryQuery}
                                goToUrl={this.goToUrl}
                                extendedStyles={styles.searchHistoryStyles}
                                {...({ closeAutoCompleteAndPopover: this.closeAutoCompleteAndPopover } as any)}
                            />
                        </StyledWrapper>
                    )}
                    {this.state.query && this.showAutocomplete && this.renderAutocomplete()}
                </Popover>
            </>
        );
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(withHistory(withTheme(SearchInput)));
